/**
 * Revision transformation utilities
 *
 * Public utilities for revision data transformations.
 * Note: API response conversion (snake_case → camelCase) happens in
 * ResourceApiClient private methods, following the MATIC pattern.
 */

/**
 * Convert numeric revision number to letter-based display label
 *
 * Implements Excel-like column naming:
 * - 1 → "RevA"
 * - 2 → "RevB"
 * - 26 → "RevZ"
 * - 27 → "RevAA"
 * - 52 → "RevAZ"
 * - 53 → "RevBA"
 * - 702 → "RevZZ"
 * - 703 → "RevAAA"
 *
 * @param revisionNumber - Numeric revision number (must be positive integer)
 * @returns Letter-based revision label (e.g., "RevA", "RevAA")
 * @throws Error if revisionNumber is not a positive integer
 *
 * @example
 * numericToLetterRevision(1)   // "RevA"
 * numericToLetterRevision(27)  // "RevAA"
 * numericToLetterRevision(100) // "RevCV"
 */
export function numericToLetterRevision(revisionNumber: number): string {
  // Validate input
  if (
    !Number.isFinite(revisionNumber) ||
    !Number.isInteger(revisionNumber) ||
    revisionNumber <= 0
  ) {
    throw new Error('Revision number must be a positive integer');
  }

  let result = '';
  let n = revisionNumber;

  // Convert to base-26 letter system (A-Z)
  // Similar to Excel column naming
  while (n > 0) {
    // Get the remainder when divided by 26 (0-25)
    // Subtract 1 to make it zero-indexed
    const remainder = (n - 1) % 26;

    // Convert to letter: 0→A, 1→B, ..., 25→Z
    const letter = String.fromCodePoint(65 + remainder);

    // Prepend letter to result (build from right to left)
    result = letter + result;

    // Move to next position
    n = Math.floor((n - 1) / 26);
  }

  return `Rev${result}`;
}

/**
 * Convert letter-based revision label to numeric revision number
 *
 * Inverse of numericToLetterRevision. Implements Excel-like column naming:
 * - "A" → 1
 * - "B" → 2
 * - "Z" → 26
 * - "AA" → 27
 * - "AZ" → 52
 * - "BA" → 53
 * - "ZZ" → 702
 * - "AAA" → 703
 *
 * @param revisionLabel - Letter-based revision label (e.g., "C", "AA")
 * @returns Numeric revision number
 * @throws Error if revisionLabel is invalid (empty, contains non-letters, etc.)
 *
 * @example
 * letterToNumericRevision('C')      // 3
 * letterToNumericRevision('AA')     // 27
 * letterToNumericRevision('CV')     // 100
 */
export function letterToNumericRevision(revisionLabel: string): number {
  if (!revisionLabel || typeof revisionLabel !== 'string') {
    throw new Error('Revision label must be a non-empty string');
  }

  // Strip "Rev" prefix if present (defensive - form validation should prevent this)
  let letters = revisionLabel.trim();
  if (letters.toLowerCase().startsWith('rev')) {
    letters = letters.slice(3);
  }

  // Validate: must contain only letters A-Z
  letters = letters.toUpperCase();
  if (!letters || !/^[A-Z]+$/.test(letters)) {
    throw new Error(
      'Revision label must contain only letters A-Z (e.g., "C", "AA")'
    );
  }

  // Convert Excel-style column name to number
  // Similar to converting from base-26 positional notation
  let result = 0;
  for (let i = 0; i < letters.length; i++) {
    const charCode = letters.codePointAt(i)!;
    const letterValue = charCode - 64; // A=1, B=2, ..., Z=26

    // Each position is worth 26^position more than the previous
    // e.g., "AB" = (1 * 26^1) + (2 * 26^0) = 26 + 2 = 28
    result = result * 26 + letterValue;
  }

  return result;
}
